import { secureStorage } from 'utils/secureStorage';
import { Environment, getEnv, isEnvironmentAllowed } from 'data_sources/constants';

export type Feature = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  managed?: boolean; // If false, feature flag is bypassed and feature is always enabled
  maxEnv?: Environment;
  orgPermissions?: string[];
  appPermissions?: string[];
};

// Default maxEnv for new features
const DEFAULT_MAX_ENV: Environment = 'stage';

class FeaturesManager {
  private static instance: FeaturesManager;
  private features: Map<string, Feature>;
  private readonly STORAGE_KEY = 'phoenix_features';
  private registeredFeatureIds: Set<string> = new Set();

  private constructor() {
    this.features = new Map();
    this.loadFeatures();
  }

  public static getInstance(): FeaturesManager {
    if (!FeaturesManager.instance) {
      FeaturesManager.instance = new FeaturesManager();
    }
    return FeaturesManager.instance;
  }

  private loadFeatures(): void {
    try {
      const storedFeatures = secureStorage.getItem(this.STORAGE_KEY);
      if (storedFeatures) {
        const features = JSON.parse(storedFeatures);
        features.forEach((feature: Feature) => {
          this.features.set(feature.id, feature);
        });
      }
    } catch (error) {
      console.error('Error loading features from storage:', error);
    }
  }

  private saveFeatures(): void {
    try {
      const features = Array.from(this.features.values());
      secureStorage.setItem(this.STORAGE_KEY, JSON.stringify(features));
    } catch (error) {
      console.error('Error saving features to storage:', error);
    }
  }

  public static registerFeature(feature: Feature): void {
    const instance = FeaturesManager.getInstance();
    const existingFeature = instance.features.get(feature.id);
    
    // Track this feature as registered in code
    instance.registeredFeatureIds.add(feature.id);
    
    if (!existingFeature) {
      // New feature - add it with default maxEnv if not specified
      const newFeature = {
        ...feature,
        maxEnv: feature.maxEnv ?? DEFAULT_MAX_ENV,
        managed: feature.managed ?? true, // Default to managed
      };
      instance.features.set(feature.id, newFeature);
    } else {
      // Existing feature - code settings always override user settings except for enabled state
      const updatedFeature = {
        ...existingFeature,
        // Code always overrides these properties
        name: feature.name,
        description: feature.description,
        maxEnv: feature.maxEnv ?? DEFAULT_MAX_ENV,
        managed: feature.managed ?? true, // Code controls whether feature is managed
        orgPermissions: feature.orgPermissions,
        appPermissions: feature.appPermissions,
        // Only preserve the user's enabled state
        enabled: existingFeature.enabled,
      };
      instance.features.set(feature.id, updatedFeature);
    }
    
    instance.saveFeatures();
  }

  public static registerFeatures(features: Feature[]): void {
    features.forEach(feature => FeaturesManager.registerFeature(feature));
  }

  /**
   * Clean up features that are no longer registered in code.
   * This should be called after all features have been registered.
   */
  public static cleanupRemovedFeatures(): void {
    const instance = FeaturesManager.getInstance();
    const featuresToRemove: string[] = [];
    
    // Find features in storage that are no longer registered in code
    for (const [featureId] of instance.features) {
      if (!instance.registeredFeatureIds.has(featureId)) {
        featuresToRemove.push(featureId);
      }
    }
    
    // Remove features that are no longer in code
    featuresToRemove.forEach(featureId => {
      instance.features.delete(featureId);
      console.log(`Removed feature '${featureId}' - no longer registered in code`);
    });
    
    if (featuresToRemove.length > 0) {
      instance.saveFeatures();
    }
  }

  /**
   * Force update all features with the latest code settings.
   * This should be called when DEFAULT_MAX_ENV or other global settings change.
   */
  public static forceUpdateAllFeatures(): void {
    const instance = FeaturesManager.getInstance();
    let hasChanges = false;
    
    // Update all existing features with the latest DEFAULT_MAX_ENV if they don't have maxEnv set
    for (const [featureId, feature] of instance.features) {
      if (!feature.maxEnv || feature.maxEnv === 'qa') {
        const updatedFeature = {
          ...feature,
          maxEnv: DEFAULT_MAX_ENV,
        };
        instance.features.set(featureId, updatedFeature);
        hasChanges = true;
        console.log(`Updated feature '${featureId}' maxEnv to ${DEFAULT_MAX_ENV}`);
      }
    }
    
    if (hasChanges) {
      instance.saveFeatures();
    }
  }

  public getFeature(id: string): Feature | undefined {
    return this.features.get(id);
  }

  public getAllFeatures(): Feature[] {
    return Array.from(this.features.values());
  }

  /**
   * Get only features that are managed (have feature flag control enabled).
   * This should be used for the admin features page to exclude unmanaged features.
   */
  public getManagedFeatures(): Feature[] {
    return Array.from(this.features.values()).filter(
      feature => feature.managed !== false
    );
  }

  public isFeatureEnabled(id: string): boolean {
    const feature = this.features.get(id);
    if (!feature) {
      return false;
    }

    // If feature is not managed (managed: false), it's always enabled
    // This allows the feature flag code to remain in place while bypassing checks
    if (feature.managed === false) {
      return true;
    }

    // Check if feature is enabled
    if (!feature.enabled) {
      return false;
    }

    // Check maxEnv constraint
    const maxEnv = feature.maxEnv ?? DEFAULT_MAX_ENV;
    if (!isEnvironmentAllowed(maxEnv)) {
      return false;
    }

    return true;
  }

  public setFeatureEnabled(id: string, enabled: boolean): void {
    const feature = this.features.get(id);
    if (feature) {
      feature.enabled = enabled;
      this.features.set(id, feature);
      this.saveFeatures();
    }
  }

  // Debug method to inspect feature state
  public debugFeatures(): void {
    console.log('=== Features Debug ===');
    const currentEnv = getEnv();
    console.log(`Current Environment: ${currentEnv}`);
    console.log(`DEFAULT_MAX_ENV: ${DEFAULT_MAX_ENV}`);
    console.log(`Registered Features: ${Array.from(this.registeredFeatureIds).join(', ')}`);
    const features = this.getAllFeatures();
    features.forEach(feature => {
      const maxEnv = feature.maxEnv ?? DEFAULT_MAX_ENV;
      const isAllowed = isEnvironmentAllowed(maxEnv);
      const isRegistered = this.registeredFeatureIds.has(feature.id);
      const isManaged = feature.managed !== false;
      console.log(`Feature: ${feature.id}`);
      console.log(`  Registered in Code: ${isRegistered}`);
      console.log(`  Managed: ${isManaged}`);
      console.log(`  Enabled: ${feature.enabled}`);
      console.log(`  Max Env: ${maxEnv}`);
      console.log(`  Environment Allowed: ${isAllowed}`);
      console.log(`  Effectively Enabled: ${this.isFeatureEnabled(feature.id)}`);
      console.log(`  Org Permissions: ${feature.orgPermissions?.join(', ') || 'none'}`);
      console.log(`  App Permissions: ${feature.appPermissions?.join(', ') || 'none'}`);
    });
    console.log('=====================');
  }

  /**
   * Get features that are stale (in storage but not registered in code)
   */
  public getStaleFeatures(): string[] {
    const staleFeatures: string[] = [];
    for (const [featureId] of this.features) {
      if (!this.registeredFeatureIds.has(featureId)) {
        staleFeatures.push(featureId);
      }
    }
    return staleFeatures;
  }

  /**
   * Force refresh all features from code (useful for debugging)
   */
  public static forceRefreshAllFeatures(): void {
    const instance = FeaturesManager.getInstance();
    console.log('Force refreshing all features...');
    
    // Clear the registered features set to force re-registration
    instance.registeredFeatureIds.clear();
    
    // This would need to be called after all features are re-registered
    // The actual re-registration would happen when modules load
    console.log('Features will be refreshed on next app load');
  }
}

export default FeaturesManager; 